import React, { Suspense, lazy, useEffect, useState } from 'react';
import axios from 'axios';
import { Box, CircularProgress } from '@mui/material';
import {
  ReceiptLong as ReceiptLongIcon,
  AddTask as AddTaskIcon,
  Dashboard as DashboardIcon,
  RequestQuote as DepartmentIcon,
  WorkspacePremium as PremiumIcon,
} from '@mui/icons-material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RuleIcon from '@mui/icons-material/Rule';
//  The routine library. A bookmark rather than a book: MenuBook is already
//  Templates, and two identical icons in one sidebar is a menu nobody can scan.
import LibraryIcon from '@mui/icons-material/CollectionsBookmark';
import ConditionIcon from '@mui/icons-material/FactCheck';

import DepartmentLayout from '../Layout/DepartmentLayout';
import { claimsEdition } from '../BaseURL';

// ── Standard edition — always present ──────────────────────────────────────
import Home from './Home';
import HospitalClaims from './HospitalClaims';
import ClaimsReport from './ClaimsReport';
import OutPatient from './OutPatient';

/* ============================================================================
 *  ONE DEPARTMENT, TWO MENUS — the subscription REPLACES, it does not stack
 * ----------------------------------------------------------------------------
 *      not subscribed    Home · Claims · Claims Report · Documentation
 *      subscribed        Dashboard · Claims Report · Documentation ·
 *                        Templates · Claim Rules · Claim Routines
 *
 *  DASHBOARD IS THE HOME. On a subscribed facility the standard Home and Claims
 *  entries are GONE, not demoted, and Dashboard takes the first position.
 *
 *  The reason is that all three were the same screen wearing different labels.
 *  Home and Dashboard both open on monthly claim volume; Claims opens the claims
 *  table — which is where the Dashboard already goes when you pick a month:
 *
 *      Dashboard -> a month -> ClaimsMenu -> ClaimEditor / Multi Vet / Repair
 *
 *  So a subscribed sidebar was offering three doors into one room, and the door
 *  labelled Claims was the one WITHOUT Multi Vet, Repair or the Advanced Filter.
 *  Verified rather than assumed: ClaimsMenu is imported by Premium/Home.js ONLY,
 *  and Premium/HospitalClaims.js references none of those three.
 *
 *  CLAIMS REPORT AND DOCUMENTATION STAY, and stay as the STANDARD components.
 *  They are genuinely their own screens, and their premium copies were measured
 *  to be near-identical files with no premium feature in them — so there is
 *  nothing to switch to. Their keys are unchanged, which matters: a role's
 *  existing component grants keep working across the switch.
 *
 *  Premium/HospitalClaims.js, Premium/ClaimsReport.js and Premium/OutPatient.js
 *  stay on disk, unreferenced and unbundled. They are where to look if those
 *  screens ever do diverge.
 *
 *  NO SECTION HEADINGS on the subscribed menu. A "PREMIUM CLAIMS" heading made
 *  sense while both editions were listed together; once the premium menu IS the
 *  menu, a heading over some of it only asks the reader to care about a
 *  distinction that no longer affects them.
 *
 *  GATED ON ENTITLEMENT, NOT PREFERENCE — `entitled` from /claimsEdition. Any
 *  failure to reach it falls back to the standard menu, so a licence lookup can
 *  never leave a claims officer with no way into Claims.
 *
 *  CODE-SPLIT. Each premium screen is lazy() and fetched on first open, so an
 *  unsubscribed facility never downloads any of the ~2 MB tree.
 *
 *  ⚠ department_components. DepartmentLayout filters this menu by that table
 *  when a role has explicit component grants, matching component_name to the
 *  `key` below. The premium keys are NEW — register them with
 *  register_premium_claims_components.sql or a granted role will not see them.
 * ========================================================================== */

const Spinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
    <CircularProgress />
  </Box>
);

// lazy() returns a component that must sit under a Suspense boundary.
// DepartmentLayout mounts `component` directly and knows nothing about
// Suspense, so each premium screen carries its own.
const lazyScreen = (loader) => {
  const Lazy = lazy(loader);
  const Wrapped = (props) => (
    <Suspense fallback={<Spinner />}>
      <Lazy {...props} />
    </Suspense>
  );
  return Wrapped;
};

// Not subscribed: exactly what this department has always shown.
const BASE_MENU = [
  { key: 'Home',           label: 'Home',          icon: <ReceiptLongIcon />, component: Home },
  { key: 'HospitalClaims', label: 'Claims',        icon: <AddTaskIcon />,     component: HospitalClaims },
  { key: 'ClaimsReport',   label: 'Claims Report', icon: <DashboardIcon />,   component: ClaimsReport },
  { key: 'OutPatient',     label: 'Documentation', icon: <AddCommentIcon />,  component: OutPatient },
];

// Subscribed: the whole menu, with Dashboard standing where Home stood.
const PREMIUM_MENU = [
  // FIRST, because it is the home now. Not just a chart — opening a month from
  // here goes to ClaimsMenu, which hosts ClaimEditor, Multi Vet, Repair and the
  // Advanced Filter. That drill-down is why there is no separate Claims entry:
  // this IS it, and the old Claims screen was the one without any of them.
  { key: 'PremiumHome',   label: 'Dashboard',      icon: <PremiumIcon />,    component: lazyScreen(() => import('./Premium/Home')) },

  // Carried over unchanged, standard components and standard keys — see the
  // header. Their premium copies add nothing, and keeping the keys means a
  // role's existing grants survive the switch.
  { key: 'ClaimsReport',  label: 'Claims Report',  icon: <DashboardIcon />,  component: ClaimsReport },
  { key: 'OutPatient',    label: 'Documentation',  icon: <AddCommentIcon />, component: OutPatient },

  // Clinical Templates — a licensed add-on in its own right, sitting here
  // because it is the insurance-side documentation library these auditors build
  // and use. Deliberately NOT hidden when unlicensed: the screen asks the
  // licence endpoint and renders a locked state, and every route behind it is
  // gated server-side. Hiding it by component permission instead would let a
  // permission grant hand out a paid feature.
  { key: 'Templates',     label: 'Templates',      icon: <MenuBookIcon />,   component: lazyScreen(() => import('./Premium/Templates')) },

  // Where the validation the other screens enforce is DEFINED: how long a
  // membership number or CCC must be per scheme, and which characters it may
  // contain. A settings screen rather than daily work, hence near the end.
  { key: 'ClaimRules',    label: 'Claim Rules',    icon: <RuleIcon />,       component: lazyScreen(() => import('./Premium/ClaimRules')) },

  // The library of treatment routines Multi Vet has saved. A basket applied to a
  // group of claims IS a standard, and one can be promoted. The daily USE of a
  // routine is inside Multi Vet, where there are claims to apply it to — this is
  // the library, not the workspace.
  { key: 'ClaimRoutines', label: 'Claim Routines', icon: <LibraryIcon />,    component: lazyScreen(() => import('./Premium/ClaimRoutines')) },

  // Condition Templates, as in Gigatt: "for this condition, in this patient, the
  // labs, drugs and procedures that go with it". Claims are CHECKED against them
  // from Multi Vet and the claim editor. Needs 'ConditionTemplates' registered for
  // department 30 (migration 075) before a role with explicit grants sees it.
  { key: 'ConditionTemplates', label: 'Condition Templates', icon: <ConditionIcon />, component: lazyScreen(() => import('./Premium/ConditionTemplates')) },
];

// Both editions live under department 30 because they ARE one department.
// DepartmentLayout's component-permission filtering keys off this id, and a
// second id would mean duplicating every role's Claims grants.
const DEPARTMENT_ID = 30;

export default function Claims() {
  // null = still asking. Rendering the standard menu first and then appending
  // would move the sidebar under the user's cursor a moment after it appeared.
  const [entitled, setEntitled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(claimsEdition)
      .then((res) => { if (!cancelled) setEntitled(!!res.data?.entitled); })
      .catch(() => { if (!cancelled) setEntitled(false); })
    return () => { cancelled = true; };
  }, []);

  if (entitled === null) return <Spinner />;

  return (
    <DepartmentLayout
      departmentId={DEPARTMENT_ID}
      departmentName="Hospital Claims"
      departmentIcon={<DepartmentIcon />}
      //  REPLACED, not concatenated. Appending was the earlier shape and it left
      //  Home and Claims sitting above a Dashboard that did both their jobs.
      menuItems={entitled ? PREMIUM_MENU : BASE_MENU}
    />
  );
}
